import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setSearchInput, setSessionExpired, setTotalPage } from "./globalSlice";
import { unauthorizedError } from "../../utils/helpers";
import SendMessageService from "../../services/api/sendMessageService";
import { toast } from "react-toastify";
const sendMessageService = new SendMessageService();

export const getAllUsers = createAsyncThunk(
  "/admin/users",
  async (
    { page, limit, filterValue, name, email, sortField, sortDirection },
    { dispatch }
  ) => {
    try {
      const response = await sendMessageService.getAllUsers({
        page,
        limit,
        filterValue,
        name,
        email,
        sortField,
        sortDirection,
      });
      if (response) {
        dispatch(setTotalPage(response?.total));
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      toast.error(error?.data?.error);
      if (error?.response && error?.response.status === 401) {
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  }
);
export const getAllUserGroups = createAsyncThunk(
  "/admin/usersGrops",
  async ({ page, limit, name, sortField, sortDirection }, { dispatch }) => {
    try {
      const response = await sendMessageService.getAllUserGroups({
        page,
        limit,
        name,
        sortField,
        sortDirection,
      });
      if (response) {
        dispatch(setTotalPage(response?.total));
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      toast.error(error?.data?.error);
      if (error?.response && error?.response.status === 401) {
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  }
);

export const sendMessage = createAsyncThunk(
  "sendMessage/send",
  async ({ payload, navigate }, { dispatch, rejectWithValue }) => {
    try {
      const response = await sendMessageService.sendMessage(payload);
      toast.success(response?.message);
      dispatch(resetState());
      // dispatch(setStep(0));
      return response.data;
    } catch (error) {
      let errorMessage;
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        errorMessage = error?.data?.error || error?.message || "Failed to send message";
        toast.error(errorMessage);
      }
      // throw error;
      return rejectWithValue(errorMessage)
    }
  }
);

export const scheduleSend = createAsyncThunk(
  "sendMessage/schedule",
  async ({ payload, navigate }, { dispatch, rejectWithValue }) => {
    try {
      const response = await sendMessageService.scheduleSend(payload);
      toast.success(response?.message);
      dispatch(resetState());
      dispatch(setSearchInput(null));
      return response.data;
    } catch (error) {
      let errorMessage;
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        errorMessage = error?.data?.error || error?.message || "Failed to schedule send";
        toast.error(errorMessage);
      }
      // throw error;
      return rejectWithValue(errorMessage)
    }
  }
);

export const getScheduled = createAsyncThunk(
  "sendMessage/get-scheduled",
  async ({ page, limit, navigate }, { dispatch, rejectWithValue }) => {
    try {
      const response = await sendMessageService.getScheduled({page, limit});
      if(response){
        dispatch(setTotalPage(response?.total));
        return response.scheduledBroadcasts;
      }
      throw new Error("Failed to fetch Scheduled Messages");
    } catch (error) {
      let errorMessage;
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        errorMessage = error?.data?.error || error?.message || "Failed to fetch scheduled messages";
        toast.error(errorMessage);
      }
      // throw error;
      return rejectWithValue(errorMessage)
    }
  }
);

export const deleteScheduled = createAsyncThunk(
  "sendMessage/delete-scheduled",
  async ({ scheduleId, navigate }, { dispatch, rejectWithValue }) => {
    try {
      const response = await sendMessageService.deleteScheduled(scheduleId);
      toast.success(response?.message);
      return response.data;
    } catch (error) {
      let errorMessage;
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        errorMessage = error?.data?.error || error?.message || "Failed to delete scheduled message";
        toast.error(errorMessage);
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateScheduled = createAsyncThunk(
  "sendMessage/update-scheduled",
  async ({ scheduleId, payload, navigate }, { dispatch, rejectWithValue }) => {
    try {
      const response = await sendMessageService.updateScheduled(scheduleId, payload);
      toast.success(response?.message);
      dispatch(resetState());
      dispatch(setSearchInput(null));
      return response.data;
    } catch (error) {
      let errorMessage;
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        errorMessage = error?.data?.error || error?.message || "Failed to update scheduled message";
        toast.error(errorMessage);
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const uploadMedia = createAsyncThunk(
  "/whatsapp/media-upload",
  async ({ formData, type, navigate, onProgress }, { dispatch }) => {
    try {
      const response = await sendMessageService.uploadMedia(
        formData,
        type,
        onProgress,
      );
      toast.success(response?.message);
      return response;
    } catch (error) {
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(error?.data?.error || "Failed to upload file. Please try again.");
      }
      throw error;
    }
  }
);
const initialState = {
  step: 0,
  allUsers: [],
  allUserGroups: [],
  allUserGroupsLoading: false,
  allUsersLoading: false,
  usersPagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  groupsPagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  selectedUsers: [],
  selectedUserGroups: [],
  scheduledMessages: [],
  activeTab: "Users",
  messagePlatform: "whatsapp",
  messageContent: "",
  parameterValues: {},
  mediaUrls: [],
  scheduleId: null,
  isEditingSchedule: false,
  scheduledAt: null,
  mediaLoading: false,
  selectedTemplate: null,
  includeInactive: false,
  isLoading: false,
  isScheduleSendLoading: false,
  isDeleteScheduledLoading: false,
  error: null,
};

const sendMessageSlice = createSlice({
  name: "sendMessage",
  initialState,
  reducers: {
    setStep: (state, action) => {
      state.step = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setSelectedUsers: (state, action) => {
      state.selectedUsers = action.payload;
    },
    setSelectedUserGroups: (state, action) => {
      state.selectedUserGroups = action.payload;
    },
    setMessagePlatform: (state, action) => {
      state.messagePlatform = action.payload;
    },
    setMessageContent: (state, action) => {
      state.messageContent = action.payload;
    },
    setSelectedTemplate: (state, action) => {
      state.selectedTemplate = action.payload;
    },
    setIncludeInactive: (state, action) => {
      state.includeInactive = action.payload;
    },
    setScheduleForEdit: (state, action) => {
      const scheduledMessage = action.payload;
      const payload = scheduledMessage?.payload || {};
      const bodyParameters = payload.parameters?.find(
        (parameter) => parameter.type === "body",
      )?.values || [];
      const headerParameters = payload.parameters?.find(
        (parameter) => parameter.type === "header",
      )?.values || [];

      state.step = 0;
      state.scheduleId = scheduledMessage?._id || scheduledMessage?.id || null;
      state.isEditingSchedule = true;
      state.scheduledAt = scheduledMessage?.scheduledAt || null;
      state.selectedUsers = payload.userIds || [];
      state.selectedUserGroups = payload.groupIds || [];
      state.messagePlatform = payload.platform || "whatsapp";
      state.includeInactive = payload.includeInactive ?? false;
      state.selectedTemplate = {
        name: payload.templateName,
        language: payload.code,
      };
      state.parameterValues = bodyParameters.reduce((parameters, value, index) => {
        parameters[index] = value;
        return parameters;
      }, {});
      state.mediaUrls = headerParameters
        .map((value) => value.link)
        .filter(Boolean);
    },
    setUsersPagination: (state, action) => {
      state.usersPagination = action.payload;
    },
    setGroupsPagination: (state, action) => {
      state.groupsPagination = action.payload;
    },
    setTemplateParameterValue: (state, action) => {
      const { index, value, placeholder, isNamed } = action.payload;
      // Case 1: Named selection
      if (isNamed) {
        state.parameterValues[index] = { isNamed: true, name: placeholder };
        return;
      }
      // Case 2: Placeholder is a number
      if (!isNaN(placeholder)) {
        state.parameterValues[index] = { value };
        return;
      }
      // Case 3: Placeholder is a string
      state.parameterValues[index] = { name: placeholder, value };
    },
    clearMedia: (state) => {
      state.mediaUrls = [];
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Users
      .addCase(getAllUsers.pending, (state) => {
        state.allUsersLoading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.allUsersLoading = false;
        state.allUsers = action.payload.data;
        state.usersPagination.total = action.payload.total;
        state.usersPagination.current = action.payload.page;
        state.usersPagination.pageSize = 10;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.allUsersLoading = false;
        state.error = action.error.message;
      })
      // User Groups
      .addCase(getAllUserGroups.pending, (state) => {
        state.allUserGroupsLoading = true;
        state.error = null;
      })
      .addCase(getAllUserGroups.fulfilled, (state, action) => {
        state.allUserGroupsLoading = false;
        state.allUserGroups = action.payload.data;
        state.groupsPagination.total = action.payload.total;
        state.groupsPagination.current = action.payload.page;
        state.groupsPagination.pageSize = 8;
      })
      .addCase(getAllUserGroups.rejected, (state, action) => {
        state.allUserGroupsLoading = false;
        state.error = action.error.message;
      })
      // Send Message
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.isLoading = false;
        // Reset form after successful send if needed
        // state.messageContent = '';
        // state.selectedUsers = [];
        // state.selectedUserGroups = [];
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      //Schedule Send
      .addCase(scheduleSend.pending, (state) => {
        state.isScheduleSendLoading = true;
        state.error = null;
      })
      .addCase(scheduleSend.fulfilled, (state) => {
        state.isScheduleSendLoading = false;
      })
      .addCase(scheduleSend.rejected, (state, action) => {
        state.isScheduleSendLoading = false;
        state.error = action.payload;
      })
      .addCase(getScheduled.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getScheduled.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scheduledMessages = action.payload;
      })
      .addCase(getScheduled.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(deleteScheduled.pending, (state) => {
        state.isDeleteScheduledLoading = true;
        state.error = null;
      })
      .addCase(deleteScheduled.fulfilled, (state, action) => {
        state.isDeleteScheduledLoading = false;
      })
      .addCase(deleteScheduled.rejected, (state, action) => {
        state.isDeleteScheduledLoading = false;
        state.error = action.error.message;
      })
      .addCase(uploadMedia.pending, (state) => {
        state.mediaLoading = true;
        state.error = null;
      })
      .addCase(uploadMedia.fulfilled, (state, action) => {
        state.mediaLoading = false;
        state.mediaUrls.push(action.payload?.url);
      })
      .addCase(uploadMedia.rejected, (state, action) => {
        state.mediaLoading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setStep,
  setActiveTab,
  setSelectedUsers,
  setSelectedUserGroups,
  setMessagePlatform,
  setMessageContent,
  setSelectedTemplate,
  setIncludeInactive,
  setScheduleForEdit,
  setUsersPagination,
  setGroupsPagination,
  resetState,
  setTemplateParameterValue,
  clearMedia,
} = sendMessageSlice.actions;

export default sendMessageSlice.reducer;
